import React, { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button } from "../../components/common/Primitives";
import api, { formatApiError, API_BASE } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import ConfirmReasonDialog from "../../components/common/ConfirmReasonDialog";
import { FileText, Download, RefreshCw, DollarSign, Plus, Ban, ChevronDown, ChevronUp } from "lucide-react";

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [gmv, setGmv] = useState(0);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null); // { invoice_id, number }
  const [generateTarget, setGenerateTarget] = useState(null); // { order_id }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/invoices?limit=200");
      setItems(r.data?.items || []);
      setGmv(r.data?.gmv_inr || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const r = await api.get("/admin/invoices/uninvoiced-orders?limit=100");
      setOrders(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOrdersLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (ordersOpen) loadOrders(); }, [ordersOpen, loadOrders]);

  const runGenerate = async ({ reason }) => {
    if (!generateTarget) return;
    try {
      await api.post(`/admin/invoices/generate/${generateTarget.order_id}`, { reason });
      toast.success(`Invoice generated for order ${generateTarget.order_id}`);
      setGenerateTarget(null);
      await Promise.all([load(), loadOrders()]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runCancel = async ({ reason }) => {
    if (!cancelTarget) return;
    try {
      await api.post(`/admin/invoices/${cancelTarget.invoice_id}/cancel`, { reason });
      toast.success(`Invoice ${cancelTarget.number} cancelled`);
      setCancelTarget(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">BILLING</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-1">Invoices</h1>
            <div className="text-sm text-ink-soft mt-1">
              Auto-generated on every paid Cashfree order. GST-compliant with CGST+SGST / IGST split.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase text-ink-muted">Lifetime GMV</div>
              <div className="font-heading text-ink text-xl inline-flex items-center gap-1">
                <DollarSign size={16} /> ₹{gmv.toLocaleString("en-IN")}
              </div>
            </div>
            <button onClick={load} className="text-xs px-2 py-1 rounded border border-navy/15" data-testid="invoices-refresh">
              <RefreshCw size={12} className="inline" />
            </button>
          </div>
        </div>

        {/* Uninvoiced orders — collapsed by default so the invoice list stays the primary view */}
        <Card testId="invoices-orders-panel" className="mb-4">
          <button
            onClick={() => setOrdersOpen((v) => !v)}
            className="w-full flex items-center justify-between text-left"
            data-testid="invoices-orders-toggle"
          >
            <div>
              <div className="overline mb-1">Uninvoiced Orders</div>
              <div className="text-xs text-ink-muted">
                Orders that don&apos;t yet have an invoice. Use Generate to force-issue (e.g. off-platform cash payments).
              </div>
            </div>
            {ordersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {ordersOpen && (
            <div className="mt-4">
              {ordersLoading ? (
                <div className="text-sm text-ink-muted italic py-2">Loading orders…</div>
              ) : orders.length === 0 ? (
                <div className="text-sm text-ink-muted italic py-2" data-testid="invoices-orders-empty">
                  Every order has an invoice. Nothing to generate.
                </div>
              ) : (
                <div className="divide-y divide-navy/[0.06]">
                  {orders.map((o) => (
                    <div key={o.order_id} className="py-2 flex items-center justify-between gap-3" data-testid={`uninvoiced-order-${o.order_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm">
                          <span className="font-mono font-bold">{o.order_id}</span>
                          <span className="ml-2 text-ink-muted">₹{Number(o.total_inr || o.amount_inr || 0).toLocaleString("en-IN")}</span>
                        </div>
                        <div className="text-[10px] font-mono text-ink-muted mt-0.5">
                          {formatDateTime(o.created_at)} · status {o.status || "?"} · {o.user_email || o.user_id || ""}
                        </div>
                      </div>
                      <Button
                        variant="gold"
                        onClick={() => setGenerateTarget({ order_id: o.order_id, total_inr: o.total_inr })}
                        data-testid={`uninvoiced-generate-${o.order_id}`}
                      >
                        <Plus size={12} className="inline mr-1" /> Generate
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card testId="invoices-list">
          {loading ? (
            <div className="text-sm text-ink-muted italic py-4">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-ink-muted italic py-4" data-testid="invoices-empty">
              No invoices generated yet. Invoices auto-fire when a payment reaches PAID status.
            </div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {items.map((inv) => {
                const isCancelled = String(inv.status || "").toUpperCase() === "CANCELLED";
                return (
                  <div key={inv.invoice_id} className="py-3 flex items-center justify-between gap-3" data-testid={`invoice-${inv.invoice_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono font-bold text-sm">{inv.invoice_number}</span>
                        <Pill tone={isCancelled ? "danger" : inv.status === "PAID" ? "success" : "muted"}>
                          {inv.status}
                        </Pill>
                        {inv.coupon_code && <Pill tone="gold">{inv.coupon_code}</Pill>}
                        {inv.intra_state ? <Pill tone="muted">Intra-state</Pill> : <Pill tone="muted">Inter-state</Pill>}
                      </div>
                      <div className="text-sm">
                        <span className="font-heading text-ink">{inv.buyer?.name || inv.buyer?.email || inv.user_id}</span>
                      </div>
                      <div className="text-[10px] font-mono text-ink-muted mt-0.5">
                        Issued {formatDateTime(inv.issued_at)} · {(inv.line_items || [])[0]?.description}
                      </div>
                      {isCancelled && inv.cancellation_reason && (
                        <div className="text-[10px] font-mono text-rose-800 mt-0.5" data-testid={`invoice-cancel-reason-${inv.invoice_id}`}>
                          Cancelled by {inv.cancelled_by} · {inv.cancellation_reason}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <div className={`font-heading ${isCancelled ? "text-ink-muted line-through" : "text-ink"}`}>₹{(inv.total_inr || 0).toLocaleString("en-IN")}</div>
                        {inv.discount_inr > 0 && (
                          <div className="text-[10px] font-mono text-emerald-800">
                            -₹{inv.discount_inr.toLocaleString("en-IN")} discount
                          </div>
                        )}
                      </div>
                      <a href={`${API_BASE}/invoices/${inv.invoice_id}/pdf`}
                        target="_blank" rel="noopener noreferrer"
                        className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1"
                        data-testid={`invoice-pdf-${inv.invoice_id}`}>
                        <Download size={12} /> PDF
                      </a>
                      {!isCancelled && (
                        <button
                          onClick={() => setCancelTarget({ invoice_id: inv.invoice_id, number: inv.invoice_number })}
                          className="px-2 py-1 rounded border border-rose-500/40 text-[11px] font-heading text-rose-800 hover:bg-rose-500/10 inline-flex items-center gap-1"
                          data-testid={`invoice-cancel-${inv.invoice_id}`}
                          title="Soft cancel — record stays for audit; PDF still downloadable"
                        >
                          <Ban size={12} /> Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <ConfirmReasonDialog
        open={!!generateTarget}
        onClose={() => setGenerateTarget(null)}
        onConfirm={runGenerate}
        title={`Generate invoice for order ${generateTarget?.order_id || ""}?`}
        description="This will mark the order PAID (if it isn't already) and issue a fresh GST invoice. Use only when payment has genuinely been received (e.g. off-platform cash / bank transfer)."
        confirmLabel="Generate invoice"
        reasonLabel="Reason (optional — recorded in audit)"
        reasonRequired={false}
        tone="emerald"
        testId="invoice-generate-dialog"
      />

      <ConfirmReasonDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={runCancel}
        title={`Cancel invoice ${cancelTarget?.number || ""}?`}
        description="This flips the invoice status to CANCELLED and records your reason. The invoice PDF stays downloadable for audit. GST invoices cannot be hard-deleted."
        confirmLabel="Cancel invoice"
        reasonLabel="Cancellation reason (required)"
        reasonRequired={true}
        tone="danger"
        testId="invoice-cancel-dialog"
      />
    </DashboardShell>
  );
}
